import React from "react";
import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { IconButton } from "@/Common/Components/IconControls/IconControls";
import { SpanControl } from "@/Common/Components/SpanControl";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { IPrescriptionTabModel } from "@/Patient/Models/UI/Medication/IPrescriptionTabModel";
import { LongTermMedicationIcon } from "./LongTermMedicationIcon";
import { MedicationListType } from "./PatientPrescriptionTab";

import styles from "./ActiveMedicationList.module.scss";

interface ICompactMedicationListProps {
    medicalSummary: IPatientMedicalSummaryModel;
    prescription: IPrescriptionTabModel;
    isAdjustButtonDisabled?: boolean;
    isRenewalButtonDisabled?: boolean;
    isMedicationClickDisabled?: boolean;
    onExternalSearchMedicationTextChange: (medicationName: string) => void;
    onAdjustMedicationPopupOpen: (medicationId: string) => void;
    onRenewMedication?: (medicationId: string) => void;
    onRenewAllMedications?: () => void;
    toggleSelectedMedication: (medicationListType: MedicationListType) => void;
}

class ActiveMedicationList extends BaseReactComponent<ICompactMedicationListProps> {
    public render(): JSX.Element {
        return (
            <div className={`tbc left-primary ${styles.container}`}>
                {this.renderHeader()}
                {this.renderMedicationList()}
            </div>
        );
    }

    private renderHeader(): JSX.Element {
        let selectedMedicationList = this.props.prescription.get("selectedMedicationList");

        return (
            <div className="banner">
                <div className="flex-container">
                    <div className={`editablecell ${styles.statusButtons}`}>
                        {this.renderMedicationListType()}
                    </div>
                    <div className={styles.headerRenewalButton}>
                        <ButtonControl
                            disabled={selectedMedicationList === MedicationListType.Completed || !!this.props.isRenewalButtonDisabled}
                            className={`btn btn-primary ${styles.smallRightMargin}`}
                            onClick={() => {
                                this.props.onRenewAllMedications ?
                                    this.props.onRenewAllMedications() :
                                    sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.LeftPaneActiveMed_RenewAll);
                            }}
                            label="R"
                            dataCy="renew-medications-button"
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderMedicationList(): JSX.Element {
        const selectedMedicationList = this.props.prescription.get("selectedMedicationList");
        const activeMeds = this.props.medicalSummary
            .get("MedicalItemsMedicationAndPrescription").toArray()
            .filter(x => x.Model.SummaryDisplayStatus == 1 || x.Model.SummaryDisplayStatus == 2);

        let medications: MedicationModel[] = [];

        if (selectedMedicationList == MedicationListType.DSQ) {
            medications = this.props.prescription
                .get("DSQMedicationModels").toArray()
                .sortByPropertyDesc(x => x.Model.RefillStartDate);
        }
        else if (selectedMedicationList == MedicationListType.Active) {
            medications = activeMeds;
        }
        else if (selectedMedicationList == MedicationListType.Completed) {
            medications = this.props.medicalSummary
                .get("MedicalItemsMedicationAndPrescription").toArray()
                .filter(medication => {
                    const summaryStatus = medication.Model.SummaryDisplayStatus;
                    const prescriptionStatus = medication.Model.PrescriptionStatusInt;

                    const isValidStatus = (
                        summaryStatus !== 1
                        && summaryStatus !== 2
                        && (prescriptionStatus === 6 || prescriptionStatus === 0)
                    );

                    return isValidStatus;
                })
                .sortByPropertyDesc(x => x.EffectiveEndDate());
        }

        medications = medications
            .filter(x => !(x.Model.isPending && x.Model.RelatedEncounterId == this.props.prescription.get("EncounterId")))
            .sort((curr: MedicationModel, next: MedicationModel) => {
                if (curr.Model.EMRTemplateSortIndex == next.Model.EMRTemplateSortIndex)
                    return 0;
                else
                    return (curr.Model.EMRTemplateSortIndex > next.Model.EMRTemplateSortIndex) ? 1 : -1;
            });

        const medicationsRender: JSX.Element[] = [];

        medications.forEach((medication: MedicationModel, idx: number) => {
            let dosageRender: JSX.Element[] = [];

            medication
                .GetDosagesDisplay({ includeRefillQuantity: true, includeDaysOrQty: false })
                .forEach((dosage, idx) => dosageRender.push(<div key={"_leftpaneDosage_" + idx}>{dosage}</div>));

            const firstDispensationString = medication.getFirstDispensationString();
            if (firstDispensationString)
                dosageRender.push(<div key="_leftpaneDosage_firstdispensation">{firstDispensationString}</div>);

            if (selectedMedicationList == MedicationListType.DSQ) {
                const dsqRenewsColor = medication.Model.DSQNumberOfRefillRemaining == 0 ? "red" : "green";
                dosageRender = [(
                    <div key={"_leftpaneDosage_" + 0} className="flex-container">
                        <div className={styles.medicationInstructions}>
                            {medication.GetInstruction()}
                        </div>
                        <div
                            className={styles.dsqRefills}
                            style={{ color: dsqRenewsColor }}
                        >
                            {`R${medication.Model.DSQNumberOfRefillRemaining}/${medication.Model.NumberOfRefill}`}
                        </div>
                    </div>
                )];
            }

            const medicationNameStyle: React.CSSProperties = medication.Model.SummaryDisplayStatus == 1 ? { color: "red" } : {};
            let hasMatchInActiveTab = true;

            if (selectedMedicationList != MedicationListType.Active)
                hasMatchInActiveTab =
                    activeMeds.some(x => x.Model.CodeGen == medication.Model.CodeGen)
                    && !IsNullOrWhiteSpace(medication.Model.CodeGen);

            if (hasMatchInActiveTab) {
                medicationNameStyle.color = "#0070d2";
                medicationNameStyle.fontWeight = "bold";
            }

            const medicationName = medication.GetName(false, selectedMedicationList == MedicationListType.DSQ);
            const isMedicationClickDisabled: boolean = !!this.props.isMedicationClickDisabled;
            const isLongTerm = cacheStore.ClinicSettings.EnableLongTermMedication && medication.Model.IsLongTerm;

            const medicationNameRender = (
                <div
                    className={`${styles.medicationNameContainer} ${!cacheStore.ClinicSettings.EnableLongTermMedication ? styles.medicationNameLabel : ""} ${isMedicationClickDisabled ? "" : "pointer"}`}
                    onClick={isMedicationClickDisabled ? null : () => this.props.onExternalSearchMedicationTextChange(medicationName)}
                >
                    {isLongTerm && (
                        <LongTermMedicationIcon dataCy={`long-term-medication-icon-${idx}`} />
                    )}
                    <div className={cacheStore.ClinicSettings.EnableLongTermMedication ? (!isLongTerm ? styles.medicationNameLabelPaddingLeft : "") : ""}><span style={medicationNameStyle}>{medicationName}</span></div>
                </div>
            );

            this.populateModelPrescriptionRenewalStatus(medication);

            medicationsRender.push(
                <div
                    key={"__a" + idx}
                    className={styles.medicationItemContainer}
                    data-cy={`activeMedicationList-item-${idx}`}
                >
                    {selectedMedicationList == MedicationListType.Completed && medication.EffectiveEndDate() != null && (
                        <div
                            className={styles.date}
                            style={{ color: "red" }}
                        >
                            {moment(medication.EffectiveEndDate()).format("YYYY-MM")}
                        </div>
                    )}
                    {selectedMedicationList == MedicationListType.DSQ && medication.Model.RefillStartDate != null && (
                        <div className={styles.date}>
                            {moment(medication.Model.RefillStartDate).format("YYYY-MM")}
                        </div>
                    )}
                    <div className="tb activemeditem" style={{ "paddingRight": "16px" }}>
                        <div className="tbc">
                            <div className="flex-column flex-start">
                                {selectedMedicationList != MedicationListType.Completed && (
                                    <div className={`editablecell ${styles.medicationNameLabelContainer}`}>
                                        <label className={styles.notCompletedMedicationLabel}>
                                            {/* <input type="checkbox" style={{ "marginTop": "5px" }} checked={medication.this.props.prescription.PrescriptionTab_IsChecked} onChange={onLeftPaneMedicationClick.bind(this, medication.this.props.prescription.Id) }/> */}
                                            {medicationNameRender}
                                        </label>
                                    </div>
                                )}
                                {selectedMedicationList == MedicationListType.Completed && (
                                    <div className={`editablecell ${styles.medicationNameLabelContainer}`}>
                                        <label className={styles.completedMedicationLabel}>
                                            {medicationNameRender}
                                        </label>
                                    </div>
                                )}
                                <div className="editablecell">
                                    <label
                                        className={styles.dosageLabel}
                                        style={{ width: selectedMedicationList != MedicationListType.Completed ? "183px" : "196px" }}
                                    >
                                        <div className={cacheStore.ClinicSettings.EnableLongTermMedication ? styles.dosageLabelPaddingLeft : ""}>
                                            {dosageRender}
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {medication.Model.PrescriptionTab_PendingStatus == null && !medication.Model.PrescriptionTab_IsRenew && (
                            <div
                                className="tbc text-right"
                                style={{ minWidth: selectedMedicationList != MedicationListType.Completed ? "83px" : "70px" }}
                            >
                                {selectedMedicationList != MedicationListType.Completed && (
                                    <IconButton
                                        buttonClassName="margin-xs-right"
                                        id={medication.Model.Id}
                                        dataCy="activeMedicationList-adjust"
                                        type="pen"
                                        color="darkblue"
                                        onClick={() => this.props.onAdjustMedicationPopupOpen(medication.Model.Id)}
                                        isDisabled={!!this.props.isAdjustButtonDisabled}
                                    />
                                )}
                                <ButtonControl
                                    id={medication.Model.Id}
                                    className="btn btn-primary push-right-5"
                                    onClick={() => {
                                        this.props.onRenewMedication ?
                                            this.props.onRenewMedication(medication.Model.Id) :
                                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.LeftPaneActiveMed_Renew, { id: medication.Model.Id });
                                    }}
                                    label="R"
                                    disabled={!!this.props.isRenewalButtonDisabled}
                                    dataCy="activeMedicationList-renewButton"
                                />
                            </div>
                        )}
                        {medication.Model.PrescriptionTab_PendingStatus == null && medication.Model.PrescriptionTab_IsRenew && (
                            <div className={`tbc text-right ${styles.actionIndicator}`}>
                                <SpanControl
                                    id="_leftPaneActionIndicator1"
                                    style={{ color: "green" }}
                                    text={cacheStore.GetString("RenewedLabel")}
                                />
                            </div>
                        )}
                        {medication.Model.PrescriptionTab_PendingStatus != null && (
                            <div className={`tbc text-right ${styles.actionIndicator}`}>
                                <SpanControl
                                    id="_leftPaneActionIndicator2"
                                    style={{ color: "red" }}
                                    text={medication.Model.PrescriptionTab_PendingStatus
                                        ? MedicationModel.PrescriptionStatusIntToValue(Number(medication.Model.PrescriptionTab_PendingStatus))
                                        : ""
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        });

        return (
            <div className="active-medication-container">
                <div
                    data-hvar="0"
                    className={`activemed-collection scrollable ${styles.medicationListContainer}`}
                >
                    {medicationsRender}
                </div>
            </div>
        );
    }

    private renderMedicationListType(): JSX.Element {
        const selectedItem = this.props.prescription.get("selectedMedicationList");

        const items = [
            new KeyValuePair(MedicationListType.Active, cacheStore.GetString("ActiveLabel")),
            sessionStore.getCurrentEmployee().isActiveSiteQuebec()
                ? new KeyValuePair(MedicationListType.DSQ, cacheStore.GetString("DSQLabel"))
                : null,
            new KeyValuePair(MedicationListType.Completed, cacheStore.GetString("CompletedLabel"))
        ].filter(p => !!p);

        const renderItems: JSX.Element[] = [];

        for (let index = 0; index < items.length; index++) {
            const item = items[index];

            if (index > 0) {
                renderItems.push(
                    <div
                        key={`spacer_${item.Key}`}
                        className={styles.spacer}
                    >
                        /
                    </div>
                );
            }

            renderItems.push(
                <div key={`item_${item.Key}`}>
                    {selectedItem != item.Key
                        ? <a onClick={() => this.props.toggleSelectedMedication(item.Key)}>{item.Value}</a>
                        : <span style={{ textDecoration: "underline" }}>{item.Value}</span>
                    }
                </div>
            );
        }

        return (
            <div className="flex-container flex-start">
                {renderItems}
            </div>
        );
    }

    private populateModelPrescriptionRenewalStatus(medication: MedicationModel): void {
        const matchingPrescription = this.props.prescription
            .get("MedicationModels")
            .find(x => (x.Model.MedicalItemId && x.Model.MedicalItemId === medication.Model.MedicalItemId));

        if (matchingPrescription && (
            medication.Model.PrescriptionTab_IsRenew == null &&
            medication.Model.PrescriptionTab_PendingStatus) == null) {
            medication.Model.PrescriptionTab_IsRenew = matchingPrescription.Model.PrescriptionTab_IsRenew;
            medication.Model.PrescriptionTab_PendingStatus = matchingPrescription.Model.PrescriptionTab_PendingStatus;
        }
    }
}

export { ActiveMedicationList };
